/**
 * Workflow and step definitions for the agreement transfer progress stepper.
 */
import { DispositionCode, HomeTransferStatusType } from 'api/transfer/types';

export type StepDefinition = {
  key: string;
  // Milestone fields on the transfer status that, when null, indicate this
  //   step is still in progress. If all fields are non-null the step is complete.
  milestoneFields?: Array<keyof HomeTransferStatusType>;
  // When this field is set and the step is active, a named sub-state is applied.
  //   Sub-states can affect the step's description and bullets.
  subStateMilestone?: { field: keyof HomeTransferStatusType; name: string };
  hideUntilCompleted?: (status: HomeTransferStatusType) => boolean;
  activeBullets?: string[];
  subStateBullets?: Record<string, string[]>;
  completedBullets?: string[];
};

export type WorkflowType = 'standard' | 'prepay' | 'payoff';

export const STANDARD_STEPS: StepDefinition[] = [
  { key: 'uccTermination', hideUntilCompleted: (s) => !!s.ucc_filing_fee_date },
  { key: 'request' },
  {
    key: 'credit',
    milestoneFields: ['credit_requested', 'credit_reviewed'],
    subStateMilestone: { field: 'credit_reviewed', name: 'reviewed' },
    activeBullets: ['bullet1', 'bullet2'],
    subStateBullets: { reviewed: ['bullet1'] },
  },
  {
    key: 'sign',
    milestoneFields: ['credit_approved', 'agreement_date_sent', 'agreement_complete_date'],
    activeBullets: ['bullet1', 'bullet2'],
    completedBullets: ['completedBullet1'],
  },
];

export const PREPAY_STEPS: StepDefinition[] = [
  { key: 'prepayRequest' },
  { key: 'prepayment', milestoneFields: ['transfer_reviewed', 'contract_paid_in_full'] },
  {
    key: 'sign',
    milestoneFields: ['agreement_date_sent', 'agreement_complete_date'],
    activeBullets: ['bullet1', 'bullet2'],
    completedBullets: ['completedBullet1'],
  },
];
export const PAYOFF_STEPS: StepDefinition[] = [
  { key: 'request' },
  {
    key: 'payoffPayment',
    milestoneFields: ['transfer_reviewed', 'contract_paid_in_full'],
    activeBullets: ['bullet1'],
  },
];

export const STEPS_BY_WORKFLOW: Record<WorkflowType, StepDefinition[]> = {
  standard: STANDARD_STEPS,
  prepay: PREPAY_STEPS,
  payoff: PAYOFF_STEPS,
};

/**
 * Returns the index of the currently active step for a given workflow.
 * Returns steps.length when the transfer is fully complete (billing onboarded).
 * defaultIndex is returned when no milestone fields have been reached yet.
 */
export function getActiveIndex(
  steps: StepDefinition[],
  status: HomeTransferStatusType,
  defaultIndex = 0,
): number {
  if (status.billing_onboarding_date) return steps.length;
  const reached = steps.findLastIndex((step) =>
    step.milestoneFields?.some((field) => status[field]),
  );
  return reached === -1 ? defaultIndex : reached;
}

/** Determines which workflow applies to this transfer based on disposition code. */
export function getWorkflowType(status: HomeTransferStatusType): WorkflowType {
  if (status.disposition_code === DispositionCode.PREPAY) return 'prepay';
  if (status.disposition_code === DispositionCode.PAYOFF) return 'payoff';
  return 'standard';
}
